import { useEffect, useRef, useState } from "react";

/**
 * Scroll-reveal hook built on IntersectionObserver.
 *
 * Returns a ref to attach to the target element and an `isVisible` flag that
 * flips to true the first time the element enters the viewport. The observer
 * disconnects immediately after firing — reveals play once, they do not
 * re-trigger when scrolling back up.
 *
 * Two safety behaviours matter more than the animation itself:
 *
 *  1. react-snap prerender. The build snapshots the DOM at DOMContentLoaded
 *     with inlineCss enabled. If an element's initial state were hidden, that
 *     hidden state would be baked into the served HTML. So `isVisible` starts
 *     true and is only lowered to false inside an effect, after we have
 *     confirmed a real browser with IntersectionObserver support. Prerender
 *     and no-JS both keep content visible.
 *
 *  2. prefers-reduced-motion. Users who ask for reduced motion skip the
 *     observer entirely and stay visible from first paint.
 *
 * @param {Object}  [options]
 * @param {number}  [options.threshold=0.15]  Visible fraction required to fire.
 * @param {string}  [options.rootMargin]      Viewport inset; the negative bottom
 *                                            value delays the reveal until the
 *                                            element is comfortably in view.
 * @param {boolean} [options.disabled=false]  Opt out; element stays visible.
 * @returns {[React.RefObject, boolean]} `[ref, isVisible]`
 */
export default function useReveal({
  threshold = 0.15,
  rootMargin = "0px 0px -80px 0px",
  disabled = false,
} = {}) {
  const ref = useRef(null);

  // Start visible — see note 1 above. Never initialise this to false.
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (disabled) return;

    const node = ref.current;
    if (!node) return;

    // No observer support (or a prerender pass): leave content visible.
    if (typeof IntersectionObserver === "undefined") return;

    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) return;

    // Element is already in view on mount (above the fold, or a mid-page
    // reload). Animating it would mean a visible flash of hidden content, so
    // leave it as-is and skip the observer.
    const rect = node.getBoundingClientRect();
    const viewportHeight = window.innerHeight || 0;
    if (rect.top < viewportHeight && rect.bottom > 0) return;

    // Safe to hide: the element is off-screen, so lowering it now is invisible
    // to the user and the reveal will play properly when scrolled into view.
    setIsVisible(false);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setIsVisible(true);
          observer.unobserve(entry.target);
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [threshold, rootMargin, disabled]);

  return [ref, isVisible];
}
